import React, { PropsWithChildren, createContext, useContext, useState } from 'react';
import { ORDER_METHODS } from '../../../services/exports/Constants';
import { CompanyDetailsInterface, OrderData, ProfileInterface } from '../../../services/exports/Interfaces';
import { useSelector } from 'react-redux';
import { selectCompany } from '../../../store/slices/InitialDataSlice';
import { selectCachedOrder } from '../../../store/slices/OrderSlice';
import useRouter from '../../../hooks/navigation/useRouter';
import useAppMethods from '../../../hooks/utility/useAppMethods';
import useOrderMethodsStatus from '../../../hooks/availability/useOrderMethodsStatus';
import { actionCreators } from '../../../store/actions';
import useAppState from '../../../hooks/global/useAppState';
import useDeliveryForm from '../../../modules/mobile/modals/delivery-address/form/useDeliveryForm';
import useDeliveryMethodValidation from './useDeliveryMethodValidation';
import { DeliveryAddressSchema } from '../../../modules/mobile/modals/delivery-address/form/schema';
import useRoomServiceLocationForm, { RoomServiceLocationSchema } from './useRoomServiceLocationForm';
import ProfileManager from '../../../services/api/ProfileManager';
import { useTranslation } from 'react-i18next';
import useSessionStorage, { SessionStorageReservedKey } from '../../../hooks/global/useSessionStorage';
import { ORDER_METHOD_INFO_COMPLETION_STATUS } from '../../../providers/OrderFlowProvider';
import { FormProvider } from 'react-hook-form';
import AuthorizationTokenStorage from '../../../services/auth/AuthorizationTokenStorage';
import useOrderFlow from '../../../hooks/order/useOrderFlow';
import ReduxActions from '../../../store/ReduxActions';
import useCompanySlug from '../../../hooks/global/useCompanySlug';

interface LocationContextType {
  loading: boolean;
  orderMethod: ORDER_METHODS;
  company?: CompanyDetailsInterface;
  scheduledFor?: string;
  setOrderMethod: (orderMethod: ORDER_METHODS) => void;
  setCompany: (company: CompanyDetailsInterface) => void;
  setScheduledFor: (scheduledFor?: string) => void;
  save: () => void;
  validationError?: string;
  setRoomServiceLocation: (id: string) => void;
  setRoomServiceDetails: (details: string) => void;
  roomServiceLocationForm: RoomServiceLocationSchema;
}

const LocationDataProviderContext = createContext<LocationContextType | undefined>({} as LocationContextType);

const LocationDataProvider = ({ children }: PropsWithChildren) => {
  const { i18n } = useTranslation();
  const selectedCompany = useSelector(selectCompany);
  const cachedOrder = useSelector(selectCachedOrder);
  const { isOrderMethodInfoComplete } = useOrderFlow();
  const { updateSlug } = useCompanySlug();
  const deliveryAddressForm = useDeliveryForm({ empty: !isOrderMethodInfoComplete });
  const { saveMenu, toggleOrderMethodModal } = useAppMethods();
  const { navigate } = useRouter();
  const [company, setCompany] = useState<CompanyDetailsInterface>(() =>
    isOrderMethodInfoComplete ? selectedCompany : null,
  );
  const { visibleOrderMethods, takeoutCurrentlyOpen, takeoutOpenForFutureOrders } = useOrderMethodsStatus(company);
  const orderMethod = visibleOrderMethods.includes(cachedOrder?.method) ? cachedOrder.method : visibleOrderMethods[0];
  const [state, setState] = useState<Pick<LocationContextType, 'orderMethod' | 'scheduledFor'>>({
    orderMethod,
    scheduledFor: cachedOrder?.scheduled_for,
  });
  const { setItem } = useSessionStorage(company);
  const setOrderMethod = (orderMethod: ORDER_METHODS) => {
    resetError();
    setState((prev) => ({ ...prev, orderMethod }));
  };
  const setScheduledFor = (scheduledFor: string) => {
    setState((prev) => ({ ...prev, scheduledFor }));
  };
  const { onComplete } = useAppState().orderMethodModalProps;
  const { validate, loading, validationError, resetError } = useDeliveryMethodValidation({
    company,
    scheduledFor: state.scheduledFor,
  });
  const {
    data: roomServiceLocationForm,
    setRoomServiceLocation,
    setRoomServiceDetails,
  } = useRoomServiceLocationForm({ company, cachedOrder });
  const cacheInfoRedux = (data: Partial<OrderData> = {}) => {
    ReduxActions.dispatch(
      actionCreators.order.updateOrder({
        company_id: company?.id,
        method: state.orderMethod,
        scheduled_for: state.scheduledFor,
        ...(state.orderMethod === ORDER_METHODS.ROOM_SERVICE && {
          room_service_location_id: roomServiceLocationForm?.roomServiceLocation?.id,
          room_service_location: roomServiceLocationForm?.roomServiceLocation,
          room_service_details: company?.table_number_hidden ? null : roomServiceLocationForm?.roomServiceDetails,
        }),
        ...deliveryAddressForm.getValues(),
        ...data,
      }),
    );
  };
  const handleSuccessSave = async (data: Partial<OrderData> = {}) => {
    if (selectedCompany.id !== company.id) {
      await updateSlug(company.slug);
      saveMenu({ menuLoaded: false });
      navigate({
        web: {
          to: company.slug,
          options: { absolute: true },
        },
      });
    }

    await updateProfile();

    cacheInfoRedux(data);

    setItem(SessionStorageReservedKey.OrderMethodInfoStatus, ORDER_METHOD_INFO_COMPLETION_STATUS.complete);
    onComplete && onComplete();
    toggleOrderMethodModal({ openModal: false });
  };
  const handleDeliveryDetailsValidation = async (data: DeliveryAddressSchema) => {
    const validatedData = await validate(data);

    if (!validatedData) {
      return;
    }

    await handleSuccessSave(validatedData);
  };
  async function updateProfile(data: Partial<ProfileInterface> = {}) {
    if (!AuthorizationTokenStorage.getToken()) {
      return;
    }

    await ProfileManager.putProfile(company.id, {
      ...deliveryAddressForm.getValues(),
      lang: i18n.language,
      ...data,
    });
  }
  const save = async () => {
    if (!takeoutCurrentlyOpen && !takeoutOpenForFutureOrders) {
      toggleOrderMethodModal({ openModal: false });
      return;
    }

    if (state.orderMethod === ORDER_METHODS.DELIVERY) {
      return deliveryAddressForm.handleSubmit(handleDeliveryDetailsValidation)();
    }

    await handleSuccessSave();
  };
  const contextValue: LocationContextType = {
    loading,
    ...state,
    company,
    setOrderMethod,
    setCompany: (company) => {
      setCompany(company);
      setScheduledFor(null);
    },
    setScheduledFor,
    save,
    validationError,
    setRoomServiceLocation,
    setRoomServiceDetails,
    roomServiceLocationForm,
  };

  return (
    <>
      <FormProvider {...deliveryAddressForm}>
        <LocationDataProviderContext.Provider value={contextValue}>{children}</LocationDataProviderContext.Provider>
      </FormProvider>
    </>
  );
};

export const useLocationData = () => useContext(LocationDataProviderContext);

export default LocationDataProvider;
